import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Loader2, 
  User, 
  ShoppingCart, 
  FileText, 
  DollarSign, 
  Calculator,
  Lock,
  Mail,
  X,
  Sparkles,
  Gift,
  TrendingUp,
  Plus,
  Search
} from "lucide-react";
import { toast } from "sonner";
import { useVat } from "@/context/VatContext";
import { useAuth } from "@/context/AuthContext";
import ReceiptModal from "@/components/ReceiptModal";
import { ProfileTab } from "./tabs/ProfileTab";
import { PurchasesTab } from "./tabs/PurchasesTab";
import { StatementTab } from "./tabs/StatementTab";
import { InvoiceMatcherTab } from "./tabs/InvoiceMatcherTab";
import { JournalTab } from "./tabs/JournalTab";
import { ReconcileTab } from "./tabs/ReconcileTab";
import { AssignSalesTab } from "./tabs/AssignSalesTab";
import { HeldFundsTab } from "./tabs/HeldFundsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default empty customer for add mode
const EMPTY_CUSTOMER = {
  name: "",
  company: "",
  contact_number: "",
  whatsapp_number: "",
  email: "",
  vat_number: "",
  farming_vat_cert_number: "",
  farming_vat_cert_expiry: "",
  company_registration_number: "",
  id_number: "",
  street_address: "",
  suburb: "",
  province: "",
  birthday: "",
  credit_limit: 0,
  discount_percent: 0,
  account_balance: 0,
  customer_level: "Standard",
  loyalty_customer_type: "default",
  has_vat_exemption: false,
  vat_exemption_number: "",
  requires_order_number: false,
  split_groceries_invoice: false,
  auto_vat_invoice: false,
  is_staff: false,
  is_blocked: false,
  till_notice: "",
};

export const CustomerDetailModal = ({ open, onClose, customer, isAddMode = false, onSaved }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const { vatEnabled } = useVat();
  
  // Customer form data
  const [formData, setFormData] = useState({});
  
  // Purchase history data
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseDateFrom, setPurchaseDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [purchaseDateTo, setPurchaseDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Include cash / card "PAID AT TILL" sales on the statement — for
  // cash-only customers who still want a monthly record of what they
  // bought. Auto-enabled when the customer has no account activity so
  // their profile isn't blank.
  const [includeCashSales, setIncludeCashSales] = useState(false);

  // Statement data
  const [statementData, setStatementData] = useState({ transactions: [], aging: {} });
  const [statementLoading, setStatementLoading] = useState(false);
  const { isAdmin } = useAuth();
  const [correctingRef, setCorrectingRef] = useState(null);
  const [statementDateFrom, setStatementDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [statementDateTo, setStatementDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [showUnreconciledOnly, setShowUnreconciledOnly] = useState(false);
  
  // Journal form data
  const [journalForm, setJournalForm] = useState({
    journal_type: "PAYMENT",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    description: "",
    email_to_customer: false,
  });
  const [processingJournal, setProcessingJournal] = useState(false);
  const [financialSummary, setFinancialSummary] = useState(null);
  
  // Reconcile data
  const [reconcileData, setReconcileData] = useState({ invoices: [] });
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [availableForRecon, setAvailableForRecon] = useState(0);
  
  // Assign Sales data
  const [unassignedSales, setUnassignedSales] = useState([]);

  // Invoice matcher
  const [matchTarget, setMatchTarget] = useState("");
  const [matchStartDate, setMatchStartDate] = useState("");
  const [matchEndDate, setMatchEndDate] = useState("");
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchMode, setMatchMode] = useState("outstanding");
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [selectedSalesToAssign, setSelectedSalesToAssign] = useState([]);
  const [assigningSales, setAssigningSales] = useState(false);
  const [convertToAccount, setConvertToAccount] = useState(false);
  const [showAllSales, setShowAllSales] = useState(false);
  const [salesSearch, setSalesSearch] = useState('');
  
  // Allocation modal state
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [allocationAmount, setAllocationAmount] = useState("");
  const [allocating, setAllocating] = useState(false);
  
  // Debug modal state
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  
  // Correct Balance modal state
  const [correctBalanceModalOpen, setCorrectBalanceModalOpen] = useState(false);
  const [correctBalanceAmount, setCorrectBalanceAmount] = useState("");
  const [correctBalanceReason, setCorrectBalanceReason] = useState("");
  const [correctingBalance, setCorrectingBalance] = useState(false);

  // Email transaction modal state
  const [emailTransactionModalOpen, setEmailTransactionModalOpen] = useState(false);
  const [emailTransactionId, setEmailTransactionId] = useState(null);
  const [emailTransactionNumber, setEmailTransactionNumber] = useState("");
  const [emailTransactionAddress, setEmailTransactionAddress] = useState("");
  const [sendingTransactionEmail, setSendingTransactionEmail] = useState(false);
  // Reprint invoice modal state
  const [reprintModalOpen, setReprintModalOpen] = useState(false);
  const [reprintTransaction, setReprintTransaction] = useState(null);
  const [loadingReprintInvoice, setLoadingReprintInvoice] = useState(null);


  // AI Promotion Suggestions modal state
  const [promotionModalOpen, setPromotionModalOpen] = useState(false);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promotionSuggestions, setPromotionSuggestions] = useState(null);
  const [applyingLoyalty, setApplyingLoyalty] = useState(false);

  useEffect(() => {
    if (open) {
      if (isAddMode) {
        setFormData({ ...EMPTY_CUSTOMER });
        setActiveTab("profile");
      } else if (customer) {
        loadCustomerDetails();
        // Reset cached data when opening for a different customer
        setFinancialSummary(null);
        setPurchaseHistory([]);
        setStatementData({ transactions: [], aging: {} });
        setReconcileData({ invoices: [] });
        setAvailableForRecon(0);
      }
    }
  }, [open, customer, isAddMode]);

  useEffect(() => {
    if (open && customer && !isAddMode && activeTab === "purchases") {
      loadPurchaseHistory();
    }
  }, [activeTab, purchaseDateFrom, purchaseDateTo, customer?.id, open]);

  useEffect(() => {
    if (open && customer && !isAddMode && activeTab === "statement") {
      loadStatement();
    }
  }, [activeTab, statementDateFrom, statementDateTo, includeCashSales, customer?.id, open]);

  useEffect(() => {
    if (open && customer && !isAddMode && activeTab === "journal") {
      loadFinancialSummary();
    }
  }, [activeTab, customer?.id, open]);

  useEffect(() => {
    if (open && customer && !isAddMode && activeTab === "reconcile") {
      loadReconcileData();
    }
  }, [activeTab, customer?.id, open]);

  useEffect(() => {
    if (open && customer && !isAddMode && activeTab === "assign") {
      loadUnassignedSales();
    }
  }, [activeTab, customer?.id, open]);

  const loadCustomerDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}`);
      setFormData(response.data);
    } catch (error) {
      console.error("Error loading customer:", error);
      toast.error("Failed to load customer details");
    } finally {
      setLoading(false);
    }
  };

  const loadPurchaseHistory = async () => {
    setPurchaseLoading(true);
    try {
      const response = await axios.get(
        `${API}/customers/${customer.id}/purchase-history?start_date=${purchaseDateFrom}&end_date=${purchaseDateTo}`
      );
      setPurchaseHistory(response.data.items || []);
    } catch (error) {
      console.error("Error loading purchase history:", error);
      toast.error("Failed to load purchase history");
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleCorrectLoyalty = async (tx) => {
    const ref = tx.inv_no;
    const current = (tx.credit || 0).toFixed(2);
    const input = window.prompt(
      `Correct loyalty credit ${ref} (now R ${current}).\n\nEnter the RIGHT amount. The statement will simply show the new amount — no reversal lines.`,
      current
    );
    if (input === null) return;
    const newAmount = parseFloat(String(input).replace(",", "."));
    if (Number.isNaN(newAmount) || newAmount < 0) return toast.error("Enter a valid amount");
    const reason = window.prompt("Reason (kept in the internal log only):", "Spend amount was too high");
    if (!reason) return;
    setCorrectingRef(ref);
    const send = (force) => axios.post(`${API}/loyalty/correct-credit/${encodeURIComponent(ref)}`, { new_amount: newAmount, reason, force });
    try {
      let res;
      try {
        res = await send(false);
      } catch (e) {
        if (e.response?.status !== 409 || !window.confirm(`${e.response.data.detail}`)) throw e;
        res = await send(true);
      }
      toast.success(`${ref} corrected: R ${res.data.old_amount.toFixed(2)} → R ${res.data.new_amount.toFixed(2)}`);
      await Promise.all([loadStatement(), loadCustomerDetails()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to correct credit");
    } finally {
      setCorrectingRef(null);
    }
  };

  const loadStatement = async () => {
    setStatementLoading(true);
    try {
      const response = await axios.get(
        `${API}/customers/${customer.id}/statement?start_date=${statementDateFrom}&end_date=${statementDateTo}&include_cash_sales=${includeCashSales}`
      );
      setStatementData(response.data);

      // Auto-enable the cash-sales toggle ONCE if this customer has no
      // account activity in the period (typical for cash-only customers).
      // Prevents a blank profile — the user asked for cash customers to
      // see their monthly transactions here too.
      if (!includeCashSales && (response.data?.transactions?.length ?? 0) === 0) {
        setIncludeCashSales(true);
      }
    } catch (error) {
      console.error("Error loading statement:", error);
      toast.error("Failed to load statement");
    } finally {
      setStatementLoading(false);
    }
  };

  const loadFinancialSummary = async () => {
    try {
      const response = await axios.get(`${API}/customers/${customer.id}/financial`);
      setFinancialSummary(response.data);
    } catch (error) {
      console.error("Error loading financial summary:", error);
    }
  };

  const loadReconcileData = async () => {
    setReconcileLoading(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}/reconcile`);
      setReconcileData(response.data);
      
      // Calculate available for reconciliation from unallocated payments/credits
      // Payments have negative balance when they have unallocated funds
      const invoices = response.data.invoices || [];
      let totalUnallocated = 0;
      
      invoices.forEach(inv => {
        // Payments, credit notes, and refunds have negative inv_total
        // Their "balance" field shows how much is still unallocated (negative = unallocated)
        if (inv.doc_type === 'PAYMENT' || inv.doc_type === 'CREDIT NOTE' || inv.doc_type === 'REFUND') {
          // balance is negative for unallocated funds, e.g. -100 means R100 available
          const unallocated = Math.abs(inv.balance || 0);
          totalUnallocated += unallocated;
        }
      });
      
      setAvailableForRecon(totalUnallocated);
    } catch (error) {
      console.error("Error loading reconcile data:", error);
      toast.error("Failed to load reconciliation data");
    } finally {
      setReconcileLoading(false);
    }
  };

  const loadUnassignedSales = async () => {
    setUnassignedLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (showAllSales) params.append('include_assigned', 'true');
      if (salesSearch) params.append('search', salesSearch);
      
      const response = await axios.get(`${API}/sales/unassigned?${params}`);
      setUnassignedSales(response.data.sales || []);
      setSelectedSalesToAssign([]);
    } catch (error) {
      console.error("Error loading sales:", error);
      toast.error("Failed to load sales");
    } finally {
      setUnassignedLoading(false);
    }
  };

  const handleAssignSales = async () => {
    if (selectedSalesToAssign.length === 0) {
      toast.error("Please select at least one sale to assign");
      return;
    }
    
    const customerId = customer?.id || formData?.id;
    const customerName = customer?.name || formData?.name;
    
    if (!customerId) {
      toast.error("Customer ID not found");
      return;
    }
    
    setAssigningSales(true);
    try {
      const response = await saveWithRetry(() => axios.post(`${API}/sales/bulk-assign`, {
        sale_ids: selectedSalesToAssign,
        customer_id: customerId,
        convert_to_account_sale: convertToAccount
      }));
      
      toast.success(`${response.data.modified_count} sales assigned to ${customerName}`);
      setSelectedSalesToAssign([]);
      setConvertToAccount(false);
      loadUnassignedSales();
      
      // Refresh customer data if we converted to account sales
      if (convertToAccount) {
        loadCustomerDetails();
      }
    } catch (error) {
      console.error("Error assigning sales:", error);
      toast.error(errorMessage(error, "Failed to assign sales"));
    } finally {
      setAssigningSales(false);
    }
  };

  const toggleSaleSelection = (saleId) => {
    setSelectedSalesToAssign(prev => 
      prev.includes(saleId) 
        ? prev.filter(id => id !== saleId)
        : [...prev, saleId]
    );
  };

  const selectAllSales = () => {
    if (selectedSalesToAssign.length === unassignedSales.length) {
      setSelectedSalesToAssign([]);
    } else {
      setSelectedSalesToAssign(unassignedSales.map(s => s.id));
    }
  };

  // Handle double-click on invoice to allocate
  const handleInvoiceDoubleClick = (invoice) => {
    // Allow allocation to PURCHASE invoices OR DEBIT NOTE journals (both represent customer debt)
    const isAllocatableTarget = 
      (invoice.doc_type === 'PURCHASE' || invoice.doc_type === 'DEBIT NOTE') && 
      invoice.balance > 0;
    
    if (!isAllocatableTarget) {
      return; // Only allow allocation to unpaid invoices or debit journals
    }
    
    if (availableForRecon <= 0) {
      toast.error("No funds available for reconciliation");
      return;
    }
    
    setSelectedInvoice(invoice);
    // Default to either the invoice balance or available funds, whichever is smaller
    const suggestedAmount = Math.min(invoice.balance, availableForRecon);
    setAllocationAmount(suggestedAmount.toFixed(2));
    setAllocationModalOpen(true);
  };

  // Process the allocation
  const handleAllocatePayment = async () => {
    const amount = parseFloat(allocationAmount);
    
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    if (amount > availableForRecon) {
      toast.error(`Cannot allocate more than available (R ${availableForRecon.toFixed(2)})`);
      return;
    }
    
    if (amount > selectedInvoice.balance) {
      toast.error(`Cannot allocate more than invoice balance (R ${selectedInvoice.balance.toFixed(2)})`);
      return;
    }
    
    setAllocating(true);
    try {
      // Determine target type based on doc_type
      const targetType = selectedInvoice.doc_type === 'DEBIT NOTE' ? 'journal' : 'sale';
      
      await saveWithRetry(() => axios.post(`${API}/customers/${customer.id}/reconcile/allocate`, {
        invoice_id: selectedInvoice.id,
        target_type: targetType,
        amount: amount,
        created_by: "ADMIN"
      }));
      
      const docLabel = selectedInvoice.doc_type === 'DEBIT NOTE' ? 'debit journal' : 'invoice';
      toast.success(`R ${amount.toFixed(2)} allocated to ${docLabel} ${selectedInvoice.inv_no}`);
      setAllocationModalOpen(false);
      setSelectedInvoice(null);
      setAllocationAmount("");
      loadReconcileData(); // Refresh the data
      if (onSaved) onSaved();
    } catch (error) {
      console.error("Error allocating payment:", error);
      toast.error(errorMessage(error, "Failed to allocate payment"));
    } finally {
      setAllocating(false);
    }
  };

  // Auto reconcile - allocate available funds to oldest invoices first
  const handleAutoRecon = async () => {
    if (availableForRecon <= 0) {
      toast.error("No funds available for reconciliation");
      return;
    }

    // Find unpaid invoices (PURCHASE or DEBIT NOTE with balance > 0)
    const unpaidInvoices = reconcileData.invoices?.filter(inv => 
      (inv.doc_type === 'PURCHASE' || inv.doc_type === 'DEBIT NOTE') && inv.balance > 0
    ).sort((a, b) => (a.date || '').localeCompare(b.date || '')) || [];

    if (unpaidInvoices.length === 0) {
      toast.info("No unpaid invoices to allocate to");
      return;
    }

    const confirmRecon = window.confirm(
      `Auto Recon will allocate R ${availableForRecon.toFixed(2)} to ${unpaidInvoices.length} unpaid invoice(s), starting from the oldest.\n\nContinue?`
    );
    if (!confirmRecon) return;

    setAllocating(true);
    try {
      let remaining = availableForRecon;
      let allocatedCount = 0;
      let totalAllocated = 0;

      for (const inv of unpaidInvoices) {
        if (remaining <= 0) break;
        
        const allocAmount = Math.min(inv.balance, remaining);
        const targetType = inv.doc_type === 'DEBIT NOTE' ? 'journal' : 'sale';
        
        await saveWithRetry(() => axios.post(`${API}/customers/${customer.id}/reconcile/allocate`, {
          invoice_id: inv.id,
          target_type: targetType,
          amount: allocAmount,
          created_by: "ADMIN-AUTORECON"
        }));
        
        remaining -= allocAmount;
        totalAllocated += allocAmount;
        allocatedCount++;
      }

      toast.success(`Auto-allocated R ${totalAllocated.toFixed(2)} to ${allocatedCount} invoice(s)`);
      loadReconcileData();
      if (onSaved) onSaved();
    } catch (error) {
      console.error("Error during auto recon:", error);
      toast.error(errorMessage(error, "Auto recon failed"));
    } finally {
      setAllocating(false);
    }
  };

  // Write off remaining balance on an invoice
  const handleWriteOff = async (inv) => {
    const reason = window.prompt(
      `Write off R ${inv.balance.toFixed(2)} on ${inv.inv_no}?\n\nEnter reason (e.g. "Refund processed", "Bad debt", "Discount given"):`,
      "Refund processed"
    );
    if (!reason) return;

    try {
      const targetType = inv.doc_type === 'DEBIT NOTE' ? 'journal' : 'sale';
      await saveWithRetry(() => axios.post(`${API}/customers/${customer.id}/reconcile/write-off`, {
        invoice_id: inv.id,
        target_type: targetType,
        reason: reason,
        created_by: "ADMIN",
      }));
      toast.success(`R ${inv.balance.toFixed(2)} written off on ${inv.inv_no}`);
      loadReconcileData();
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(errorMessage(error, "Write-off failed"));
    }
  };

  // Debug balance function
  const handleDebugBalance = async () => {
    if (!customer?.id) return;
    setDebugLoading(true);
    setDebugModalOpen(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}/debug-balance`);
      setDebugData(response.data);
    } catch (error) {
      console.error("Error fetching debug data:", error);
      toast.error("Failed to load debug data");
      setDebugData({ error: error.response?.data?.detail || error.message });
    } finally {
      setDebugLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    if (!formData.name) {
      toast.error("Customer name is required");
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        credit_limit: parseFloat(formData.credit_limit) || 0,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        opening_balance: parseFloat(formData.account_balance) || 0,
        loyalty_customer_type: formData.loyalty_customer_type === "default" ? null : formData.loyalty_customer_type,
      };

      if (isAddMode) {
        // Create new customer
        await saveWithRetry(() => axios.post(`${API}/customers`, submitData));
        toast.success("Customer created successfully");
      } else {
        // Update existing customer
        await saveWithRetry(() => axios.put(`${API}/customers/${customer.id}`, submitData));
        toast.success("Customer saved successfully");
      }
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error(errorMessage(error, isAddMode ? "Failed to create customer" : "Failed to save customer"));
    } finally {
      setSaving(false);
    }
  };

  // Correct customer balance - back-calculates the correct opening balance
  const handleCorrectBalance = async () => {
    const amount = parseFloat(correctBalanceAmount);
    if (isNaN(amount)) {
      toast.error("Please enter a valid balance amount");
      return;
    }

    setCorrectingBalance(true);
    try {
      const response = await axios.post(`${API}/customers/${customer.id}/correct-balance`, {
        correct_balance: amount,
        reason: correctBalanceReason || "Manual balance correction"
      });
      
      toast.success(response.data.message);
      
      // Update local state with new balance
      setFormData(prev => ({
        ...prev,
        account_balance: response.data.new_opening_balance
      }));
      
      setCorrectBalanceModalOpen(false);
      setCorrectBalanceAmount("");
      setCorrectBalanceReason("");
      
      // Refresh statement and financial data
      if (activeTab === "statement") {
        loadStatement();
      }
      if (activeTab === "journal") {
        loadFinancialSummary();
      }
      
      // Notify parent to refresh
      if (onSaved) onSaved();
    } catch (error) {
      console.error("Error correcting balance:", error);
      const errorMsg = error.response?.data?.detail || "Failed to correct balance";
      toast.error(errorMsg);
    } finally {
      setCorrectingBalance(false);
    }
  };

  // Email a specific transaction/invoice
  const openEmailTransactionModal = (saleId, invNo) => {
    setEmailTransactionId(saleId);
    setEmailTransactionNumber(invNo);
    setEmailTransactionAddress(formData.email || "");
    setEmailTransactionModalOpen(true);
  };

  const handleEmailTransaction = async () => {
    if (!emailTransactionAddress || !emailTransactionAddress.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingTransactionEmail(true);
    try {
      await axios.post(`${API}/documents/email`, {
        document_type: "sales",
        document_id: emailTransactionId,
        email: emailTransactionAddress
      });
      toast.success(`Invoice ${emailTransactionNumber} emailed to ${emailTransactionAddress}`);
      setEmailTransactionModalOpen(false);
    } catch (error) {
      console.error("Error emailing transaction:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingTransactionEmail(false);
    }
  };

  // Reprint a specific invoice from purchase history
  const handleReprintInvoice = async (item) => {
    const transactionNumber = item?.inv_no;
    if (!transactionNumber) {
      toast.error("Invoice number not found");
      return;
    }

    setLoadingReprintInvoice(transactionNumber);
    try {
      const response = await axios.get(`${API}/sales/${encodeURIComponent(transactionNumber)}`);
      const sale = response.data || {};

      const resolvedTransaction = {
        ...sale,
        customer_name: sale.customer_name || formData.name || customer?.name || "",
        customer_account_number: sale.customer_account_number || formData.account_number || customer?.account_number || "",
        customer_address: sale.customer_address || formData.street_address || customer?.street_address || "",
        customer_suburb: sale.customer_suburb || formData.suburb || customer?.suburb || "",
        customer_province: sale.customer_province || formData.province || customer?.province || "",
        customer_contact: sale.customer_contact || formData.contact_number || customer?.contact_number || "",
        customer_vat_number: sale.customer_vat_number || formData.vat_number || customer?.vat_number || "",
        customer_email: sale.customer_email || formData.email || customer?.email || ""
      };

      setReprintTransaction(resolvedTransaction);
      setReprintModalOpen(true);
    } catch (error) {
      console.error("Error loading invoice for reprint:", error);
      toast.error(error.response?.data?.detail || "Failed to load invoice");
    } finally {
      setLoadingReprintInvoice(null);
    }
  };

  const handleUnassignSale = async (saleId, invNo) => {
    if (!window.confirm(`Remove sale ${invNo} from this customer and convert to CASH SALE?`)) return;
    try {
      await axios.post(`${API}/sales/${saleId}/unassign-customer`);
      toast.success(`${invNo} removed from customer`);
      loadPurchaseHistory();
    } catch (error) {
      console.error("Unassign error:", error);
      toast.error(error.response?.data?.detail || "Failed to unassign sale");
    }
  };

  const handleMatchInvoices = async () => {
    if (!matchTarget || parseFloat(matchTarget) <= 0) {
      toast.error("Enter a valid target amount");
      return;
    }
    setMatchLoading(true);
    setMatchResults(null);
    try {
      const res = await axios.post(`${API}/customers/${customer.id}/match-invoices`, {
        target_amount: parseFloat(matchTarget),
        start_date: matchStartDate,
        end_date: matchEndDate,
        match_mode: matchMode,
      });
      setMatchResults(res.data);
      if (res.data.matches_found === 0) {
        toast.info(`No exact match found (checked ${res.data.invoices_checked} invoices by ${matchMode === 'outstanding' ? 'outstanding amount' : 'invoice total'})`);
      } else {
        toast.success(`Found ${res.data.matches_found} matching combination(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Match failed");
    } finally {
      setMatchLoading(false);
    }
  };

  // Fetch AI promotion suggestions for this customer
  const handleGetPromotionSuggestions = async () => {
    if (!customer?.id) return;
    
    setPromotionLoading(true);
    setPromotionModalOpen(true);
    setPromotionSuggestions(null);
    
    try {
      const response = await axios.get(
        `${API}/personalized-promotions/customer/${customer.id}/ai-suggestions`
      );
      setPromotionSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching promotion suggestions:", error);
      toast.error(error.response?.data?.detail || "Failed to get promotion suggestions");
      setPromotionSuggestions({ error: error.response?.data?.detail || error.message });
    } finally {
      setPromotionLoading(false);
    }
  };

  // Apply loyalty credit to customer account (full or partial)
  const handleUseLoyalty = async (amount) => {
    const loyaltyBal = formData.loyalty_credit_balance || customer?.loyalty_credit_balance || 0;
    if (loyaltyBal <= 0) {
      toast.error("No loyalty credit available");
      return;
    }
    const applyAmount = (typeof amount === "number" && amount > 0) ? Math.min(amount, loyaltyBal) : loyaltyBal;
    const isPartial = applyAmount < loyaltyBal - 0.001;
    const confirmMsg = isPartial
      ? `Apply R${applyAmount.toFixed(2)} of the R${loyaltyBal.toFixed(2)} loyalty credit as a payment on this account?\n\nRemaining loyalty after: R${(loyaltyBal - applyAmount).toFixed(2)}`
      : `Apply R${applyAmount.toFixed(2)} loyalty credit as a payment on this account?\n\nThis will:\n- Create a PAYMENT journal entry\n- Deduct from loyalty balance`;
    if (!window.confirm(confirmMsg)) return;

    setApplyingLoyalty(true);
    try {
      const res = await saveWithRetry(() => axios.post(`${API}/customers/${customer.id}/loyalty-pay-account`, {
        amount: applyAmount
      }));
      toast.success(res.data.message);
      setFormData(prev => ({ ...prev, loyalty_credit_balance: res.data.new_loyalty_balance }));
      loadReconcileData();
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to apply loyalty credit"));
    } finally {
      setApplyingLoyalty(false);
    }
  };


  const handleEmailStatementToSelf = async () => {
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/email/statement`, {
        customer_id: customer.id,
        send_to_self: true,
        start_date: statementDateFrom,
        end_date: statementDateTo,
        include_cash_sales: includeCashSales,
      });
      const to = (res.data?.results || []).map(r => r.email).join(', ');
      toast.success(`Statement copy sent to ${to || 'your email'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send statement");
    } finally {
      setSendingEmail(false);
    }
  };

  // Email statement to customer
  const handleEmailStatement = async () => {
    if (!formData.email) {
      toast.error("Customer does not have an email address");
      return;
    }

    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/email/statement`, {
        customer_id: customer.id,
        recipient_email: formData.email,
        start_date: statementDateFrom,
        end_date: statementDateTo,
        include_cash_sales: includeCashSales,
      });
      const data = res.data || {};
      const sent = data.sent_count;
      const total = data.total_recipients;
      const recipients = (data.results || [])
        .filter(r => r.status === 'success')
        .map(r => r.email);
      if (recipients.length > 0) {
        toast.success(`Statement sent to ${recipients.join(', ')}${total && sent < total ? ` (${sent}/${total})` : ''}`);
      } else {
        toast.success(`Statement sent to ${formData.email}`);
      }
    } catch (error) {
      console.error("Error sending statement:", error);
      const errorMsg = error.response?.data?.detail || "Failed to send statement";
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  // Print statement
  const handlePrintStatement = () => {
    if (!statementData.transactions?.length) {
      toast.error("No transactions to print");
      return;
    }

    const printContent = `
      <html>
        <head>
          <title>Account Statement - ${formData.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            h1 { font-size: 18px; margin-bottom: 5px; }
            h2 { font-size: 14px; color: #666; margin-bottom: 20px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .company-info { text-align: right; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
            th { background: #333; color: white; font-weight: bold; }
            .text-right { text-align: right; }
            .debit { color: #dc2626; }
            .credit { color: #16a34a; }
            .total-row { background: #f5f5f5; font-weight: bold; }
            .aging { display: flex; gap: 15px; margin-top: 20px; }
            .aging-box { border: 1px solid #ddd; padding: 10px; text-align: center; min-width: 70px; }
            .aging-label { font-size: 10px; color: #666; }
            .aging-value { font-size: 14px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>ACCOUNT STATEMENT</h1>
              <p>Period: ${statementDateFrom} to ${statementDateTo}</p>
            </div>
            <div class="company-info">
              <strong>${formData.account_number || ''}</strong><br/>
              ${formData.name}<br/>
              ${formData.contact_number || ''}<br/>
              ${formData.email || ''}
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>DocType</th>
                <th>InvNo</th>
                <th class="text-right">Debit</th>
                <th class="text-right">Credit</th>
                <th class="text-right">Balance</th>
                <th class="text-right">Paid</th>
                <th>Date</th>
                <th>User</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${statementData.transactions.map(tx => `
                <tr>
                  <td>${tx.doc_type}</td>
                  <td>${tx.inv_no}</td>
                  <td class="text-right debit">${tx.debit > 0 ? 'R ' + tx.debit.toFixed(2) : '-'}</td>
                  <td class="text-right credit">${tx.credit > 0 ? 'R ' + tx.credit.toFixed(2) : '-'}</td>
                  <td class="text-right">R ${tx.balance?.toFixed(2) || '0.00'}</td>
                  <td class="text-right">${tx.paid > 0 ? 'R ' + tx.paid.toFixed(2) : '-'}</td>
                  <td>${tx.date}</td>
                  <td>${tx.user}</td>
                  <td>${tx.description || ''}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2">TOTALS</td>
                <td class="text-right debit">R ${statementData.total_debit?.toFixed(2) || '0.00'}</td>
                <td class="text-right credit">R ${statementData.total_credit?.toFixed(2) || '0.00'}</td>
                <td class="text-right">R ${statementData.balance_owing?.toFixed(2) || '0.00'}</td>
                <td colspan="4"></td>
              </tr>
            </tbody>
          </table>
          
          <div class="aging">
            <div class="aging-box">
              <div class="aging-label">Current</div>
              <div class="aging-value">R ${statementData.aging?.current?.toFixed(2) || '0.00'}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">30 Days</div>
              <div class="aging-value">R ${statementData.aging?.days_30?.toFixed(2) || '0.00'}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">60 Days</div>
              <div class="aging-value">R ${statementData.aging?.days_60?.toFixed(2) || '0.00'}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">90 Days</div>
              <div class="aging-value">R ${statementData.aging?.days_90?.toFixed(2) || '0.00'}</div>
            </div>
            <div class="aging-box">
              <div class="aging-label">120+ Days</div>
              <div class="aging-value">R ${statementData.aging?.days_120?.toFixed(2) || '0.00'}</div>
            </div>
            <div class="aging-box" style="background: #fef2f2; border-color: #fca5a5;">
              <div class="aging-label">Balance Owing</div>
              <div class="aging-value" style="color: #dc2626;">R ${statementData.balance_owing?.toFixed(2) || '0.00'}</div>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Print purchase history / invoices
  const handlePrintPurchases = () => {
    if (!purchaseHistory?.length) {
      toast.error("No purchase history to print");
      return;
    }

    const printContent = `
      <html>
        <head>
          <title>Purchase History - ${formData.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            h1 { font-size: 18px; margin-bottom: 5px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
            th { background: #1e40af; color: white; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>PURCHASE HISTORY</h1>
              <p>${formData.name} | ${formData.contact_number || ''}</p>
              <p>Period: ${purchaseDateFrom} to ${purchaseDateTo}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>InvNo</th>
                <th>Date</th>
                <th>PLU</th>
                <th>Description</th>
                <th class="text-center">Qty</th>
                <th class="text-right">Retail</th>
                <th class="text-right">Total</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              ${purchaseHistory.map(item => `
                <tr>
                  <td>${item.inv_no}</td>
                  <td>${item.date}</td>
                  <td>${item.plu}</td>
                  <td>${item.description}</td>
                  <td class="text-center">${item.qty}</td>
                  <td class="text-right">R ${item.retail?.toFixed(2) || '0.00'}</td>
                  <td class="text-right">R ${item.total?.toFixed(2) || '0.00'}</td>
                  <td>${item.user}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleProcessJournal = async () => {
    if (!journalForm.amount || parseFloat(journalForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setProcessingJournal(true);
    try {
      const res = await saveWithRetry(() => axios.post(`${API}/customers/${customer.id}/journal`, {
        journal_type: journalForm.journal_type,
        amount: parseFloat(journalForm.amount),
        date: journalForm.date,
        description: journalForm.description || `${journalForm.journal_type} entry`,
        created_by: "ADMIN",
        email_to_customer: !!journalForm.email_to_customer,
      }));

      const emailRes = res?.data?.email_result;
      if (journalForm.email_to_customer) {
        if (emailRes?.status === "success") {
          toast.success(`Journal processed — emailed to customer`);
        } else if (emailRes?.status === "blocked") {
          toast.warning("Journal processed — email blocked (emails disabled in settings).");
        } else if (emailRes?.status === "error") {
          toast.warning(`Journal processed — email failed: ${emailRes.message || "see logs"}`);
        } else if (emailRes?.message) {
          toast.success(`Journal processed — ${emailRes.message}`);
        } else {
          toast.success("Journal entry processed successfully");
        }
      } else {
        toast.success("Journal entry processed successfully");
      }

      setJournalForm({
        journal_type: "PAYMENT",
        amount: "",
        date: new Date().toISOString().split('T')[0],
        description: "",
        email_to_customer: false,
      });
      loadFinancialSummary();
      if (onSaved) onSaved();
    } catch (error) {
      console.error("Error processing journal:", error);
      toast.error(errorMessage(error, "Failed to process journal entry"));
    } finally {
      setProcessingJournal(false);
    }
  };

  const exportStatementCSV = () => {
    if (!statementData.transactions?.length) {
      toast.error("No data to export");
      return;
    }

    const headers = ["DocType", "InvNo", "Debit", "Credit", "Balance", "Paid", "Date", "User", "Description"];
    const csvContent = [
      headers.join(","),
      ...statementData.transactions.map(tx => [
        tx.doc_type,
        tx.inv_no,
        tx.debit?.toFixed(2) || "0.00",
        tx.credit?.toFixed(2) || "0.00",
        tx.balance?.toFixed(2) || "0.00",
        tx.paid?.toFixed(2) || "0.00",
        tx.date,
        tx.user,
        `"${tx.description || ''}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `statement_${formData.name}_${statementDateFrom}_${statementDateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Statement exported");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateStr;
    }
  };

  if (!customer && !isAddMode) return null;

  return (
    <> 
      <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-bold">
                {isAddMode ? "Add New Customer" : (formData.name || customer?.name)}
              </DialogTitle>
              {!isAddMode && (
                <p className="text-sm text-purple-600 font-medium">
                  Account: {formData.account_number || customer?.cust_id}
                  <span className="ml-4 inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                    Loyalty: R {(formData.loyalty_credit_balance || customer?.loyalty_credit_balance || 0).toFixed(2)}
                  </span>
                </p>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          {isAddMode ? (
            <TabsList className="grid grid-cols-1 mx-6 mt-4 w-fit">
              <TabsTrigger value="profile" className="flex items-center gap-1">
                <User className="h-4 w-4" />
                Profile
              </TabsTrigger>
            </TabsList>
          ) : (
            <TabsList className="grid grid-cols-7 mx-6 mt-4">
              <TabsTrigger value="profile" className="flex items-center gap-1">
                <User className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="purchases" className="flex items-center gap-1">
                <ShoppingCart className="h-4 w-4" />
                Purchases
              </TabsTrigger>
              <TabsTrigger value="statement" className="flex items-center gap-1">
                <FileText className="h-4 w-4" />
                Statement
              </TabsTrigger>
              <TabsTrigger value="journal" className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                Journal
              </TabsTrigger>
              <TabsTrigger value="held" className="flex items-center gap-1" data-testid="tab-held-funds">
                <Lock className="h-4 w-4" />
                Held Funds
              </TabsTrigger>
              <TabsTrigger value="reconcile" className="flex items-center gap-1">
                <Calculator className="h-4 w-4" />
                Reconcile
              </TabsTrigger>
              <TabsTrigger value="match" className="flex items-center gap-1">
                <Search className="h-4 w-4" />
                Match Invoices
              </TabsTrigger>
            </TabsList>
          )}

          <div className="flex-1 overflow-auto p-6">
            {/* Profile Tab */}
            <TabsContent value="profile" className="mt-0 space-y-6">
              <ProfileTab
                loading={loading} isAddMode={isAddMode} formData={formData}
                customer={customer} saving={saving}
                handleFormChange={handleFormChange} handleSaveProfile={handleSaveProfile}
                onClose={onClose} handleGetPromotionSuggestions={handleGetPromotionSuggestions}
                setCorrectBalanceAmount={setCorrectBalanceAmount}
                setCorrectBalanceReason={setCorrectBalanceReason}
                setCorrectBalanceModalOpen={setCorrectBalanceModalOpen}
              />
            </TabsContent>

            {/* Purchases Tab */}
            <TabsContent value="purchases" className="mt-0">
              <PurchasesTab
                formData={formData} customerId={customer?.id}
                purchaseDateFrom={purchaseDateFrom} setPurchaseDateFrom={setPurchaseDateFrom}
                purchaseDateTo={purchaseDateTo} setPurchaseDateTo={setPurchaseDateTo}
                purchaseLoading={purchaseLoading} purchaseHistory={purchaseHistory}
                handlePrintPurchases={handlePrintPurchases} handleReprintInvoice={handleReprintInvoice}
                openEmailTransactionModal={openEmailTransactionModal} handleUnassignSale={handleUnassignSale}
                loadingReprintInvoice={loadingReprintInvoice} formatDate={formatDate}
              />
            </TabsContent>

            {/* Statement Tab */}
            <TabsContent value="statement" className="mt-0">
              <StatementTab
                formData={formData}
                statementDateFrom={statementDateFrom} setStatementDateFrom={setStatementDateFrom}
                statementDateTo={statementDateTo} setStatementDateTo={setStatementDateTo}
                showUnreconciledOnly={showUnreconciledOnly} setShowUnreconciledOnly={setShowUnreconciledOnly}
                includeCashSales={includeCashSales} setIncludeCashSales={setIncludeCashSales}
                statementLoading={statementLoading} statementData={statementData}
                exportStatementCSV={exportStatementCSV} handlePrintStatement={handlePrintStatement}
                handleEmailStatement={handleEmailStatement} handleEmailStatementToSelf={handleEmailStatementToSelf} sendingEmail={sendingEmail}
                formatDate={formatDate}
                canCorrectLoyalty={isAdmin} onCorrectLoyalty={handleCorrectLoyalty} correctingRef={correctingRef}
              />
            </TabsContent>

            {/* Match Invoices Tab — also hosts "Assign Sales" since they're related */}
            <TabsContent value="match" className="mt-0 space-y-8">
              <InvoiceMatcherTab
                matchTarget={matchTarget} setMatchTarget={setMatchTarget}
                matchStartDate={matchStartDate} setMatchStartDate={setMatchStartDate}
                matchEndDate={matchEndDate} setMatchEndDate={setMatchEndDate}
                matchMode={matchMode} setMatchMode={setMatchMode}
                matchResults={matchResults} matchLoading={matchLoading}
                handleMatchInvoices={handleMatchInvoices}
              />

              <div className="border-t-2 border-gray-200 pt-6">
                <AssignSalesTab
                  formData={formData} customer={customer}
                  unassignedLoading={unassignedLoading} unassignedSales={unassignedSales}
                  selectedSalesToAssign={selectedSalesToAssign} assigningSales={assigningSales}
                  convertToAccount={convertToAccount} setConvertToAccount={setConvertToAccount}
                  showAllSales={showAllSales} setShowAllSales={setShowAllSales}
                  salesSearch={salesSearch} setSalesSearch={setSalesSearch}
                  loadUnassignedSales={loadUnassignedSales} handleAssignSales={handleAssignSales}
                  toggleSaleSelection={toggleSaleSelection} selectAllSales={selectAllSales}
                />
              </div>
            </TabsContent>

            {/* Journal Tab */}
            <TabsContent value="journal" className="mt-0">
              <JournalTab
                formData={formData} customer={customer}
                financialSummary={financialSummary}
                journalForm={journalForm} setJournalForm={setJournalForm}
                processingJournal={processingJournal}
                handleProcessJournal={handleProcessJournal}
                onClose={onClose}
              />
            </TabsContent>

            {/* Held Funds Tab — freeze a portion of the customer's credit
                so it can't be spent at the till on unrelated purchases. */}
            <TabsContent value="held" className="mt-0">
              <HeldFundsTab customerId={customer?.id} />
            </TabsContent>

            {/* Reconcile Tab */}
            <TabsContent value="reconcile" className="mt-0">
              <ReconcileTab
                reconcileLoading={reconcileLoading} reconcileData={reconcileData}
                availableForRecon={availableForRecon} allocating={allocating}
                handleAutoRecon={handleAutoRecon} handleDebugBalance={handleDebugBalance}
                handleInvoiceDoubleClick={handleInvoiceDoubleClick} handleWriteOff={handleWriteOff}
                formatDate={formatDate}
                loyaltyBalance={formData.loyalty_credit_balance || customer?.loyalty_credit_balance || 0}
                onUseLoyalty={handleUseLoyalty}
                applyingLoyalty={applyingLoyalty}
              />
            </TabsContent>

            {/* Assign Sales Tab */}
            <TabsContent value="assign" className="mt-0">
              {/* Moved into the Match Invoices tab */}
            </TabsContent>
          </div>
        </Tabs>

        {/* Allocation Modal */}
        {allocationModalOpen && selectedInvoice && (
          <Dialog open={allocationModalOpen} onOpenChange={() => setAllocationModalOpen(false)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Allocate Payment to Invoice</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="bg-gray-100 p-4 rounded space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Invoice:</span>
                    <span className="font-mono font-bold">{selectedInvoice.inv_no}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Invoice Total:</span>
                    <span className="font-mono">R {selectedInvoice.inv_total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Already Paid:</span>
                    <span className="font-mono text-green-600">R {selectedInvoice.inv_amt_paid?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600 font-bold">Outstanding:</span>
                    <span className="font-mono font-bold text-red-600">R {selectedInvoice.balance?.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <div className="flex justify-between">
                    <span className="text-green-700">Available for Recon:</span>
                    <span className="font-mono font-bold text-green-700">R {availableForRecon.toFixed(2)}</span>
                  </div>
                </div>

                <div>
                  <Label>Amount to Allocate</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">R</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max={Math.min(selectedInvoice.balance, availableForRecon)}
                      value={allocationAmount}
                      onChange={(e) => setAllocationAmount(e.target.value)}
                      className="text-xl font-mono"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Max: R {Math.min(selectedInvoice.balance, availableForRecon).toFixed(2)}
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setAllocationModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAllocatePayment}
                    disabled={allocating}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {allocating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Allocate Payment
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Debug Balance Modal */}
        {debugModalOpen && (
          <Dialog open={debugModalOpen} onOpenChange={setDebugModalOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  🔍 Balance Debug - {debugData?.customer_name || customer?.name}
                </DialogTitle>
              </DialogHeader>
              
              {debugLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : debugData?.error ? (
                <div className="p-4 bg-red-50 text-red-700 rounded">
                  Error: {debugData.error}
                </div>
              ) : debugData ? (
                <div className="space-y-4">
                  {/* Calculated Balance */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-bold text-lg text-blue-800">Calculated Balance</h3>
                    <p className="text-3xl font-mono font-bold text-blue-900">
                      R {debugData.calculated_balance?.toFixed(2)}
                    </p>
                  </div>

                  {/* Formula */}
                  <div className="p-3 bg-gray-100 rounded text-sm font-mono">
                    {debugData.formula}
                  </div>

                  {/* Breakdown */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-200 px-4 py-2 font-bold">Balance Breakdown</div>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b bg-yellow-50">
                          <td className="px-4 py-2">TRUE Opening Balance</td>
                          <td className="px-4 py-2 text-right font-mono font-bold">R {debugData.breakdown?.true_opening_balance?.toFixed(2)}</td>
                        </tr>
                        <tr className="border-b text-gray-500 text-xs">
                          <td className="px-4 py-2 pl-8">↳ Stored account_balance: R {debugData.breakdown?.stored_account_balance?.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono">+ POS receipts: R {debugData.breakdown?.total_account_payments?.toFixed(2)}</td>
                        </tr>
                        <tr className="border-b bg-green-50">
                          <td className="px-4 py-2">+ Journal Debits</td>
                          <td className="px-4 py-2 text-right font-mono text-green-700">R {debugData.breakdown?.journal_debits?.toFixed(2)}</td>
                        </tr>
                        <tr className="border-b bg-green-50">
                          <td className="px-4 py-2">+ Net Sales ({debugData.counts?.sales} invoices)</td>
                          <td className="px-4 py-2 text-right font-mono text-green-700">R {debugData.breakdown?.net_sales?.toFixed(2)}</td>
                        </tr>
                        {debugData.breakdown?.payments_within_sales > 0 && (
                          <tr className="border-b text-gray-500 text-xs">
                            <td className="px-4 py-2 pl-8">↳ Gross sales: R {debugData.breakdown?.gross_sales?.toFixed(2)}</td>
                            <td className="px-4 py-2 text-right font-mono">- Account payments in sales: R {debugData.breakdown?.payments_within_sales?.toFixed(2)}</td>
                          </tr>
                        )}
                        <tr className="border-b bg-red-50">
                          <td className="px-4 py-2">- Refunds ({debugData.counts?.refunds})</td>
                          <td className="px-4 py-2 text-right font-mono text-red-700">R {debugData.breakdown?.total_refunds?.toFixed(2)}</td>
                        </tr>
                        <tr className="border-b bg-red-50">
                          <td className="px-4 py-2">- Manual Payments ({debugData.counts?.customer_payments})</td>
                          <td className="px-4 py-2 text-right font-mono text-red-700">R {debugData.breakdown?.total_customer_payments?.toFixed(2)}</td>
                        </tr>
                        <tr className="border-b bg-red-50">
                          <td className="px-4 py-2">- POS Account Receipts ({debugData.counts?.account_payments})</td>
                          <td className="px-4 py-2 text-right font-mono text-red-700">R {debugData.breakdown?.total_account_payments?.toFixed(2)}</td>
                        </tr>
                        <tr className="border-b bg-red-50">
                          <td className="px-4 py-2">- Journal Credits (deduped)</td>
                          <td className="px-4 py-2 text-right font-mono text-red-700">R {debugData.breakdown?.journal_credits_deduped?.toFixed(2)}</td>
                        </tr>
                        <tr className="bg-blue-100 font-bold">
                          <td className="px-4 py-2">= BALANCE</td>
                          <td className="px-4 py-2 text-right font-mono">R {debugData.calculated_balance?.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Deduplication Info */}
                  {debugData.deduplication_applied && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                      <p className="font-bold text-orange-800">⚠️ Duplicate Payments Detected!</p>
                      <p className="text-sm text-orange-700">
                        {debugData.counts?.duplicate_journals_detected} journal entries matched POS receipts and were skipped to avoid double-counting.
                      </p>
                      <p className="text-sm font-mono mt-1">
                        Raw journal credits: R {debugData.breakdown?.journal_credits_raw?.toFixed(2)} → 
                        After dedup: R {debugData.breakdown?.journal_credits_deduped?.toFixed(2)}
                        <span className="text-green-700 font-bold ml-2">(Saved R {debugData.breakdown?.duplicate_amount_skipped?.toFixed(2)})</span>
                      </p>
                    </div>
                  )}

                  {/* Extra Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-yellow-50 rounded">
                      <p className="text-sm text-yellow-800">Total amount_paid on sales records:</p>
                      <p className="font-mono font-bold">R {debugData.breakdown?.total_amount_paid_on_sales?.toFixed(2)}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded">
                      <p className="text-sm text-purple-800">Total reconciled:</p>
                      <p className="font-mono font-bold">R {debugData.breakdown?.total_reconciled?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Duplicate Journals Detail */}
                  {debugData.duplicate_journals?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden border-orange-300">
                      <div className="bg-orange-100 px-4 py-2 font-bold text-orange-800">Skipped Duplicate Journals</div>
                      <div className="p-2 text-sm">
                        {debugData.duplicate_journals.map((j, i) => (
                          <div key={i} className="flex justify-between py-1 border-b last:border-0">
                            <span>{j.ref} ({j.type})</span>
                            <span className="font-mono text-orange-600">R {j.amount?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Account Payments */}
                  {debugData.account_payments_detail?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-200 px-4 py-2 font-bold">Recent POS Account Receipts</div>
                      <div className="p-2 text-sm">
                        {debugData.account_payments_detail.map((p, i) => (
                          <div key={i} className="flex justify-between py-1 border-b last:border-0">
                            <span>{p.ref}</span>
                            <span className="font-mono">R {p.amount?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Journals */}
                  {debugData.journals_detail?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-200 px-4 py-2 font-bold">Recent Journals</div>
                      <div className="p-2 text-sm">
                        {debugData.journals_detail.map((j, i) => (
                          <div key={i} className="flex justify-between py-1 border-b last:border-0">
                            <span>{j.ref} ({j.type})</span>
                            <span className="font-mono">R {j.amount?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
              
              <div className="flex justify-end pt-4">
                <Button variant="outline" onClick={() => setDebugModalOpen(false)}>
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Correct Balance Modal */}
        {correctBalanceModalOpen && (
          <Dialog open={correctBalanceModalOpen} onOpenChange={setCorrectBalanceModalOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Correct Customer Balance
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Enter what the customer's <strong>current balance</strong> should be. 
                    The system will automatically calculate and adjust the opening balance 
                    to make it correct.
                  </p>
                </div>

                <div>
                  <Label className="text-base font-semibold">What should the balance be?</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xl font-bold">R</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={correctBalanceAmount}
                      onChange={(e) => setCorrectBalanceAmount(e.target.value)}
                      placeholder="-671.00"
                      className="text-xl font-bold"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Negative = You owe the customer | Positive = Customer owes you
                  </p>
                </div>

                <div>
                  <Label>Reason for correction (optional)</Label>
                  <Input
                    value={correctBalanceReason}
                    onChange={(e) => setCorrectBalanceReason(e.target.value)}
                    placeholder="e.g., Verified against bank statements"
                    className="mt-1"
                  />
                </div>

                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This will adjust the opening balance to make the 
                    calculated total match your target. A journal entry will be created 
                    to record this correction.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCorrectBalanceModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCorrectBalance}
                  disabled={correctingBalance || !correctBalanceAmount}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {correctingBalance ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Correcting...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4 mr-2" />
                      Correct Balance
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Email Transaction Modal */}
        {emailTransactionModalOpen && (
          <Dialog open={emailTransactionModalOpen} onOpenChange={setEmailTransactionModalOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Invoice
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <p className="text-sm text-gray-600">
                  Send invoice <strong>{emailTransactionNumber}</strong> to the customer via email.
                </p>

                <div>
                  <Label className="text-sm font-medium">Email Address</Label>
                  <Input
                    type="email"
                    value={emailTransactionAddress}
                    onChange={(e) => setEmailTransactionAddress(e.target.value)}
                    placeholder="customer@example.com"
                    className="mt-1"
                    autoFocus
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEmailTransactionModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleEmailTransaction}
                  disabled={sendingTransactionEmail || !emailTransactionAddress}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sendingTransactionEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* AI Promotion Suggestions Modal */}
        {promotionModalOpen && (
          <Dialog open={promotionModalOpen} onOpenChange={setPromotionModalOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
                <div className="flex items-center gap-3 text-white">
                  <Sparkles className="h-6 w-6" />
                  <div>
                    <DialogTitle className="text-xl text-white">AI Promotion Suggestions</DialogTitle>
                    <p className="text-purple-200 text-sm">{formData.name}</p>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="flex-1 overflow-auto py-4">
                {promotionLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin text-purple-600 mb-4" />
                    <p className="text-gray-600">Analyzing purchase history...</p>
                    <p className="text-sm text-gray-400">This may take a few seconds</p>
                  </div>
                ) : promotionSuggestions?.error ? (
                  <div className="text-center py-8">
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg">
                      <p className="font-medium">Error getting suggestions</p>
                      <p className="text-sm mt-1">{promotionSuggestions.error}</p>
                    </div>
                  </div>
                ) : promotionSuggestions?.message ? (
                  <div className="text-center py-8">
                    <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg">
                      <Gift className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                      <p className="font-medium">{promotionSuggestions.message}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Based on section */}
                    {promotionSuggestions?.based_on?.length > 0 && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <TrendingUp className="h-4 w-4 inline mr-1" />
                          Based on frequently purchased items:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {promotionSuggestions.based_on.map((item, idx) => (
                            <span 
                              key={idx} 
                              className="bg-white px-2 py-1 rounded text-xs border border-gray-200"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggestions */}
                    <div className="space-y-3">
                      {promotionSuggestions?.suggestions?.map((suggestion, idx) => (
                        <div 
                          key={idx} 
                          className="border rounded-lg p-4 hover:border-purple-300 hover:bg-purple-50/50 transition-colors"
                          data-testid={`promotion-suggestion-${idx}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Gift className="h-5 w-5 text-purple-500" />
                                <h4 className="font-semibold text-gray-800">
                                  {suggestion.product_name}
                                </h4>
                              </div>
                              <p className="text-sm text-gray-600 mt-1 ml-7">
                                {suggestion.reason}
                              </p>
                              {suggestion.matched_product && (
                                <p className="text-xs text-purple-600 mt-1 ml-7">
                                  Matched in catalog: {suggestion.matched_product.name || suggestion.matched_product.plu_code}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                                {suggestion.suggested_discount}% off
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Fallback notice */}
                    {promotionSuggestions?.fallback && (
                      <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                        <p className="text-sm text-amber-700">
                          <span className="font-medium">Note:</span> AI service unavailable. 
                          Showing suggestions based on purchase frequency.
                        </p>
                      </div>
                    )}

                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mt-4">
                      <p className="text-sm text-blue-700">
                        <span className="font-medium">Next Step:</span> Use these suggestions to 
                        manually create personalized promotions for this customer in your promotions module.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setPromotionModalOpen(false)}
                >
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>

      <ReceiptModal
        open={reprintModalOpen}
        onClose={() => {
          setReprintModalOpen(false);
          setReprintTransaction(null);
        }}
        transaction={reprintTransaction}
        vatEnabled={vatEnabled}
        autoPrint={false}
      />
    </>
  );
};

export default CustomerDetailModal;
